import React, { useRef } from 'react'
import { Box, Dialog, Grid, SvgIcon, Typography } from '@material-ui/core'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { quickApplyModalStyled } from './quick-apply-component.style'
import { Form, Formik } from 'formik'
import { PhoneField, TextField } from '~/components/fields'
import { composeT, email, maxLength, phone, required } from '~/common/validator'
import { Button } from '~/components/button'
import { useEffectOnce } from 'react-use'
import { quickApplyStore } from './quick-apply.store'
import { handleLoadReCaptcha } from '~/common/helpers'
import { NotifyDialog } from '~/components/notify'
import { appStore, routerStore } from '~/stores'
import { PATHS } from '~/common/constants'
import { observer } from 'mobx-react-lite'
import { CloseIconDialog } from '~/components/icons'
import * as yup from 'yup'
import { logClickEvent } from '~/common/tracking/event-client.tracking'
import { COMMON_EVENT_CLICK_TRACKING } from '~/common/constants/tracking.constant'
import { isFunction } from 'lodash'
import { showMappingAddress } from '~/common/helpers/mapping.helper'

const validateSchema = yup.object({
	firstName: yup.string().required('Please enter your first name').min(2, 'Please ensure First name has at least 2 characters'),
	lastName: yup.string().required('Please enter your last name').min(2, 'Please ensure Last name has at least 2 characters'),
	email: yup.string().required('Please enter your email').email('Invalid email format, please recheck'),
})

export const QuickApply = observer(({ open, setClose }) => {
	const { t } = useTranslation()
	const formRef = useRef(null)
	const { toggleConfirm, confirm, currentJobID, currentJob } = quickApplyStore
	useEffectOnce(() => {
		handleLoadReCaptcha()
	})

	const handleOnClose = () => {
		setClose(false)
		logClickEvent(COMMON_EVENT_CLICK_TRACKING.closeBtnApplyForThisJob, {
			job_id: currentJobID,
			is_account_created: false, // null HERE
		})

		if (isFunction(formRef.current.resetForm)) {
			formRef.current.resetForm()
		}
	}

	const handleOnClickSignIn = async (values) => {
		const { email } = values || {}
		await toggleConfirm(false)
		if (currentJobID) {
			window.localStorage.setItem('quickApplyJob', JSON.stringify({ jobId: currentJobID, email }))
		}
		await routerStore.goPage(PATHS.common.signin)
		logClickEvent(COMMON_EVENT_CLICK_TRACKING.signInBtn, {
			job_id: currentJobID,
			is_account_created: false, // null HERE
		})
	}
	return (
		<>
			<Formik
				innerRef={formRef}
				validationSchema={validateSchema}
				initialValues={{}}
				onSubmit={async (variables, { setErrors, resetForm }) => {
					await quickApplyStore.checkExistAccount({ ...variables }, { setErrors }, resetForm, setClose)
					await logClickEvent(COMMON_EVENT_CLICK_TRACKING.confirmToApplyBtnApplyForThisJob, {
						job_id: currentJobID,
						is_account_created: false, // null HERE
					})
				}}
			>
				{({ handleSubmit, isSubmitting, values, setValues, resetForm }) => {
					return (
						<>
							<Dialog open={open} onClose={handleOnClose} css={quickApplyModalStyled}>
								<Box m={2}>
									<Grid container className="wrap-title">
										<Grid item xs={12} sm={12} className="title">
											<Typography>
												{currentJob?.atsSource === 'symplr'
													? 'Apply for this shift'
													: `Apply Now - ${currentJob?.jobGeneral?.title} in ${showMappingAddress(currentJob?.jobGeneral?.address)}`}
											</Typography>
										</Grid>

										<div
											onClick={handleOnClose}
											style={{
												position: 'absolute',
												top: 15,
												right: 15,
												cursor: 'pointer',
											}}
										>
											<SvgIcon component={CloseIconDialog} />
										</div>

										<Grid item xs={12}>
											<Typography
												css={[
													appStore.getTheme.typography.p1,
													{
														color: appStore.getTheme.colors.content.dark,
														margin: '10px 0',
													},
												]}
											>
												{currentJob?.atsSource === 'symplr'
													? 'Please provide the following information, as this will be necessary for a staffing specialist to proceed with your request. '
													: 'By submitting your contact information, you confirm that the details provided are accurate and belong to you. Please ensure all information is correct, as it will be used to communicate regarding your application.'}
											</Typography>
										</Grid>
									</Grid>
									<Form onSubmit={handleSubmit}>
										<div className="wrap-form">
											<div>
												<Grid container spacing={2}>
													<Grid item xs={6}>
														<TextField
															className={'firstName'}
															name="firstName"
															placeholder={t('$PLACEHOLDERS.FIRST_NAME')}
															label={t('FIRST_NAME')}
															validate={composeT(t)(required, maxLength(60))}
														/>
													</Grid>
													<Grid item xs={6}>
														<TextField
															className={'lastName'}
															name="lastName"
															placeholder={t('$PLACEHOLDERS.LAST_NAME')}
															label={t('LAST_NAME')}
															validate={composeT(t)(required, maxLength(60))}
														/>
													</Grid>
													<Grid item xs={6}>
														<TextField
															className={'emailField'}
															name={`email`}
															label={t('EMAIL_ADDRESS')}
															placeholder={t('$PLACEHOLDERS.EMAIL')}
															validate={composeT(t)(required, email)}
														/>
													</Grid>
													<Grid item xs={6}>
														<PhoneField
															validate={composeT(t)(phone)}
															endLabel={
																<Typography color="textSecondary" variant="p1">
																	{t('OPTIONAL')}
																</Typography>
															}
															placeholder={t('$PLACEHOLDERS.PHONE')}
															name={`phone`}
															label={t('PHONE_NUMBER')}
														/>
													</Grid>
												</Grid>
											</div>
										</div>

										<Grid container spacing={2}>
											<Grid style={{ marginTop: 20, marginBottom: 20 }} item justify="space-between" container>
												<Grid item xs={12} style={{ paddingLeft: 4 }}>
													<Button loading={isSubmitting} type="submit" eventId="signup_btn">
														Confirm to apply
													</Button>
												</Grid>
											</Grid>
										</Grid>
									</Form>
								</Box>
							</Dialog>
							<NotifyDialog
								onClose={() => toggleConfirm(false)}
								showButtonClose={true}
								open={confirm}
								okText="SIGN_IN"
								onOk={() => handleOnClickSignIn(values)}
								title={t('$MESSAGES.QUICK_APPLY_CREATED')}
								onCancel={() => toggleConfirm(false)}
								children={currentJob?.atsSource === 'symplr' ? t('$MESSAGES.QUICK_APPLY_SL_EXIST_CONTENT') : t('$MESSAGES.QUICK_APPLY_EXIST_CONTENT')}
							/>
						</>
					)
				}}
			</Formik>
		</>
	)
})
